import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn.js';
function clamp(value, min, max) {
    let next = value;
    if (min != null)
        next = Math.max(min, next);
    if (max != null)
        next = Math.min(max, next);
    return next;
}
function decimals(step) {
    const text = String(step);
    const index = text.indexOf('.');
    return index === -1 ? 0 : text.length - index - 1;
}
/**
 * A number with its unit and stepper buttons: setpoints, timeouts, pulse
 * durations, thresholds. Right-aligned tabular digits; min, max and step from
 * the schema; the stepper keeps to the step's precision. Put the range in the
 * hint ("10–600 s").
 */
const NumberInput = React.forwardRef(({ value, onChange, min, max, step = 1, unit, invalid, disabled, className, inputClassName, id, onFocus, onBlur, onKeyDown, ...props }, ref) => {
    const { t } = useTranslation();
    const [text, setText] = React.useState(value == null ? '' : String(value));
    const [focused, setFocused] = React.useState(false);
    React.useEffect(() => {
        if (!focused)
            setText(value == null ? '' : String(value));
    }, [value, focused]);
    const commit = (raw) => {
        if (raw.trim() === '') {
            onChange(null);
            return;
        }
        const parsed = Number(raw);
        if (Number.isNaN(parsed)) {
            setText(value == null ? '' : String(value));
            return;
        }
        const next = clamp(parsed, min, max);
        setText(String(next));
        onChange(next);
    };
    const nudge = (direction) => {
        const base = value ?? (min ?? 0);
        const next = clamp(Number((base + direction * step).toFixed(decimals(step))), min, max);
        setText(String(next));
        onChange(next);
    };
    return (_jsxs("div", { "data-slot": "number-input", className: cn('inline-flex h-control w-fit max-w-full items-stretch overflow-hidden rounded-md border border-input bg-card transition-[color,box-shadow] hover:border-input-hover', 'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50', invalid && 'border-destructive ring-destructive/30', disabled && 'opacity-50', className), children: [_jsx("input", { ref: ref, id: id, type: "number", inputMode: "decimal", role: "spinbutton", "aria-valuenow": value ?? undefined, "aria-valuemin": min, "aria-valuemax": max, "aria-invalid": invalid || undefined, min: min, max: max, step: step, disabled: disabled, ...props, value: text, 
                // A caller's handlers run after the primitive's own, so a form
                // adapter's `field.onBlur` sees the committed value and never
                // replaces commit-on-blur.
                onFocus: (event) => {
                    setFocused(true);
                    onFocus?.(event);
                }, onBlur: (event) => {
                    setFocused(false);
                    commit(text);
                    onBlur?.(event);
                }, onChange: (event) => setText(event.target.value), onKeyDown: (event) => {
                    if (event.key === 'Enter')
                        commit(text);
                    onKeyDown?.(event);
                }, className: cn('w-[5.5rem] min-w-0 bg-transparent px-2 text-right text-ui tnum outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none', inputClassName) }), unit && (_jsx("span", { className: "flex items-center border-l border-border px-2 text-caption text-muted-foreground", "aria-hidden": "true", children: unit })), _jsx("button", { type: "button", tabIndex: -1, "aria-label": t('kit.increment'), disabled: disabled || (max != null && value != null && value >= max), onClick: () => nudge(1), className: "grid w-7 min-w-7 max-w-7 flex-none place-items-center border-l border-border text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40", children: _jsx(Plus, { className: "size-3.5", "aria-hidden": "true" }) }), _jsx("button", { type: "button", tabIndex: -1, "aria-label": t('kit.decrement'), disabled: disabled || (min != null && value != null && value <= min), onClick: () => nudge(-1), className: "grid w-7 min-w-7 max-w-7 flex-none place-items-center border-l border-border text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40", children: _jsx(Minus, { className: "size-3.5", "aria-hidden": "true" }) })] }));
});
NumberInput.displayName = 'NumberInput';
export { NumberInput };
//# sourceMappingURL=number-input.js.map